/**
 * Country flags for the sports whose competitors have no club badge — tennis, golf, MMA and
 * NASCAR (DKPLAT-606). A competitor is shown by nationality where a team sport shows a crest.
 *
 * Feeds describe a country every which way: tennis sends "France" / "FRA", golf "UNITED STATES"
 * and "CHINESE TAIPEI", NASCAR "ENGLAND". `countryToIso` folds all of them onto one flag code:
 * the lowercase ISO 3166-1 alpha-2 code, or `gb-eng` / `gb-sct` / `gb-wls` / `gb-nir` for the
 * home nations golf lists separately. Only codes with artwork are returned, so a caller never
 * builds a URL that 404s.
 */

/** [flag code, ISO alpha-3 ('' for the UK home nations), English name] — one per flag file. */
const COUNTRIES: ReadonlyArray<readonly [string, string, string]> = [
  ['ad', 'AND', 'Andorra'],
  ['ae', 'ARE', 'United Arab Emirates'],
  ['af', 'AFG', 'Afghanistan'],
  ['ag', 'ATG', 'Antigua and Barbuda'],
  ['ai', 'AIA', 'Anguilla'],
  ['al', 'ALB', 'Albania'],
  ['am', 'ARM', 'Armenia'],
  ['ao', 'AGO', 'Angola'],
  ['ar', 'ARG', 'Argentina'],
  ['at', 'AUT', 'Austria'],
  ['au', 'AUS', 'Australia'],
  ['aw', 'ABW', 'Aruba'],
  ['az', 'AZE', 'Azerbaijan'],
  ['ba', 'BIH', 'Bosnia and Herzegovina'],
  ['bb', 'BRB', 'Barbados'],
  ['bd', 'BGD', 'Bangladesh'],
  ['be', 'BEL', 'Belgium'],
  ['bf', 'BFA', 'Burkina Faso'],
  ['bg', 'BGR', 'Bulgaria'],
  ['bh', 'BHR', 'Bahrain'],
  ['bi', 'BDI', 'Burundi'],
  ['bj', 'BEN', 'Benin'],
  ['bm', 'BMU', 'Bermuda'],
  ['bn', 'BRN', 'Brunei'],
  ['bo', 'BOL', 'Bolivia'],
  ['br', 'BRA', 'Brazil'],
  ['bs', 'BHS', 'Bahamas'],
  ['bt', 'BTN', 'Bhutan'],
  ['bw', 'BWA', 'Botswana'],
  ['by', 'BLR', 'Belarus'],
  ['bz', 'BLZ', 'Belize'],
  ['ca', 'CAN', 'Canada'],
  ['cd', 'COD', 'DR Congo'],
  ['cf', 'CAF', 'Central African Republic'],
  ['cg', 'COG', 'Congo'],
  ['ch', 'CHE', 'Switzerland'],
  ['ci', 'CIV', 'Ivory Coast'],
  ['ck', 'COK', 'Cook Islands'],
  ['cl', 'CHL', 'Chile'],
  ['cm', 'CMR', 'Cameroon'],
  ['cn', 'CHN', 'China'],
  ['co', 'COL', 'Colombia'],
  ['cr', 'CRI', 'Costa Rica'],
  ['cu', 'CUB', 'Cuba'],
  ['cv', 'CPV', 'Cape Verde'],
  ['cw', 'CUW', 'Curacao'],
  ['cy', 'CYP', 'Cyprus'],
  ['cz', 'CZE', 'Czech Republic'],
  ['de', 'DEU', 'Germany'],
  ['dj', 'DJI', 'Djibouti'],
  ['dk', 'DNK', 'Denmark'],
  ['dm', 'DMA', 'Dominica'],
  ['do', 'DOM', 'Dominican Republic'],
  ['dz', 'DZA', 'Algeria'],
  ['ec', 'ECU', 'Ecuador'],
  ['ee', 'EST', 'Estonia'],
  ['eg', 'EGY', 'Egypt'],
  ['er', 'ERI', 'Eritrea'],
  ['es', 'ESP', 'Spain'],
  ['et', 'ETH', 'Ethiopia'],
  ['fi', 'FIN', 'Finland'],
  ['fj', 'FJI', 'Fiji'],
  ['fk', 'FLK', 'Falkland Islands'],
  ['fm', 'FSM', 'Micronesia'],
  ['fr', 'FRA', 'France'],
  ['ga', 'GAB', 'Gabon'],
  ['gb', 'GBR', 'United Kingdom'],
  ['gb-eng', '', 'England'],
  ['gb-nir', '', 'Northern Ireland'],
  ['gb-sct', '', 'Scotland'],
  ['gb-wls', '', 'Wales'],
  ['gd', 'GRD', 'Grenada'],
  ['ge', 'GEO', 'Georgia'],
  ['gh', 'GHA', 'Ghana'],
  ['gl', 'GRL', 'Greenland'],
  ['gm', 'GMB', 'Gambia'],
  ['gn', 'GIN', 'Guinea'],
  ['gq', 'GNQ', 'Equatorial Guinea'],
  ['gr', 'GRC', 'Greece'],
  ['gt', 'GTM', 'Guatemala'],
  ['gu', 'GUM', 'Guam'],
  ['gw', 'GNB', 'Guinea Bissau'],
  ['gy', 'GUY', 'Guyana'],
  ['hk', 'HKG', 'Hong Kong'],
  ['hn', 'HND', 'Honduras'],
  ['hr', 'HRV', 'Croatia'],
  ['ht', 'HTI', 'Haiti'],
  ['hu', 'HUN', 'Hungary'],
  ['id', 'IDN', 'Indonesia'],
  ['ie', 'IRL', 'Ireland'],
  ['il', 'ISR', 'Israel'],
  ['im', 'IMN', 'Isle of Man'],
  ['in', 'IND', 'India'],
  ['iq', 'IRQ', 'Iraq'],
  ['ir', 'IRN', 'Iran'],
  ['is', 'ISL', 'Iceland'],
  ['it', 'ITA', 'Italy'],
  ['jm', 'JAM', 'Jamaica'],
  ['jo', 'JOR', 'Jordan'],
  ['jp', 'JPN', 'Japan'],
  ['ke', 'KEN', 'Kenya'],
  ['kg', 'KGZ', 'Kyrgyzstan'],
  ['kh', 'KHM', 'Cambodia'],
  ['ki', 'KIR', 'Kiribati'],
  ['km', 'COM', 'Comoros'],
  ['kn', 'KNA', 'Saint Kitts and Nevis'],
  ['kp', 'PRK', 'North Korea'],
  ['kr', 'KOR', 'South Korea'],
  ['kw', 'KWT', 'Kuwait'],
  ['ky', 'CYM', 'Cayman Islands'],
  ['kz', 'KAZ', 'Kazakhstan'],
  ['la', 'LAO', 'Laos'],
  ['lb', 'LBN', 'Lebanon'],
  ['lc', 'LCA', 'Saint Lucia'],
  ['li', 'LIE', 'Liechtenstein'],
  ['lk', 'LKA', 'Sri Lanka'],
  ['lr', 'LBR', 'Liberia'],
  ['ls', 'LSO', 'Lesotho'],
  ['lt', 'LTU', 'Lithuania'],
  ['lu', 'LUX', 'Luxembourg'],
  ['lv', 'LVA', 'Latvia'],
  ['ly', 'LBY', 'Libya'],
  ['ma', 'MAR', 'Morocco'],
  ['mc', 'MCO', 'Monaco'],
  ['md', 'MDA', 'Moldova'],
  ['me', 'MNE', 'Montenegro'],
  ['mg', 'MDG', 'Madagascar'],
  ['mh', 'MHL', 'Marshall Islands'],
  ['mk', 'MKD', 'North Macedonia'],
  ['ml', 'MLI', 'Mali'],
  ['mm', 'MMR', 'Myanmar'],
  ['mn', 'MNG', 'Mongolia'],
  ['mo', 'MAC', 'Macau'],
  ['mq', 'MTQ', 'Martinique'],
  ['mr', 'MRT', 'Mauritania'],
  ['ms', 'MSR', 'Montserrat'],
  ['mt', 'MLT', 'Malta'],
  ['mu', 'MUS', 'Mauritius'],
  ['mv', 'MDV', 'Maldives'],
  ['mw', 'MWI', 'Malawi'],
  ['mx', 'MEX', 'Mexico'],
  ['my', 'MYS', 'Malaysia'],
  ['mz', 'MOZ', 'Mozambique'],
  ['na', 'NAM', 'Namibia'],
  ['ne', 'NER', 'Niger'],
  ['ng', 'NGA', 'Nigeria'],
  ['ni', 'NIC', 'Nicaragua'],
  ['nl', 'NLD', 'Netherlands'],
  ['no', 'NOR', 'Norway'],
  ['np', 'NPL', 'Nepal'],
  ['nr', 'NRU', 'Nauru'],
  ['nz', 'NZL', 'New Zealand'],
  ['om', 'OMN', 'Oman'],
  ['pa', 'PAN', 'Panama'],
  ['pe', 'PER', 'Peru'],
  ['pg', 'PNG', 'Papua New Guinea'],
  ['ph', 'PHL', 'Philippines'],
  ['pk', 'PAK', 'Pakistan'],
  ['pl', 'POL', 'Poland'],
  ['pr', 'PRI', 'Puerto Rico'],
  ['ps', 'PSE', 'Palestine'],
  ['pt', 'PRT', 'Portugal'],
  ['pw', 'PLW', 'Palau'],
  ['py', 'PRY', 'Paraguay'],
  ['qa', 'QAT', 'Qatar'],
  ['ro', 'ROU', 'Romania'],
  ['rs', 'SRB', 'Serbia'],
  ['ru', 'RUS', 'Russia'],
  ['rw', 'RWA', 'Rwanda'],
  ['sa', 'SAU', 'Saudi Arabia'],
  ['sb', 'SLB', 'Solomon Islands'],
  ['sc', 'SYC', 'Seychelles'],
  ['sd', 'SDN', 'Sudan'],
  ['se', 'SWE', 'Sweden'],
  ['sg', 'SGP', 'Singapore'],
  ['si', 'SVN', 'Slovenia'],
  ['sk', 'SVK', 'Slovakia'],
  ['sl', 'SLE', 'Sierra Leone'],
  ['sm', 'SMR', 'San Marino'],
  ['sn', 'SEN', 'Senegal'],
  ['so', 'SOM', 'Somalia'],
  ['sr', 'SUR', 'Suriname'],
  ['ss', 'SSD', 'South Sudan'],
  ['st', 'STP', 'Sao Tome and Principe'],
  ['sv', 'SLV', 'El Salvador'],
  ['sx', 'SXM', 'Sint Maarten'],
  ['sy', 'SYR', 'Syria'],
  ['sz', 'SWZ', 'Eswatini'],
  ['tc', 'TCA', 'Turks and Caicos Islands'],
  ['td', 'TCD', 'Chad'],
  ['tg', 'TGO', 'Togo'],
  ['th', 'THA', 'Thailand'],
  ['tj', 'TJK', 'Tajikistan'],
  ['tl', 'TLS', 'East Timor'],
  ['tm', 'TKM', 'Turkmenistan'],
  ['tn', 'TUN', 'Tunisia'],
  ['to', 'TON', 'Tonga'],
  ['tr', 'TUR', 'Turkey'],
  ['tt', 'TTO', 'Trinidad and Tobago'],
  ['tv', 'TUV', 'Tuvalu'],
  ['tw', 'TWN', 'Taiwan'],
  ['tz', 'TZA', 'Tanzania'],
  ['ua', 'UKR', 'Ukraine'],
  ['ug', 'UGA', 'Uganda'],
  ['us', 'USA', 'United States'],
  ['uy', 'URY', 'Uruguay'],
  ['uz', 'UZB', 'Uzbekistan'],
  ['va', 'VAT', 'Vatican City'],
  ['vc', 'VCT', 'Saint Vincent and the Grenadines'],
  ['ve', 'VEN', 'Venezuela'],
  ['vg', 'VGB', 'British Virgin Islands'],
  ['vn', 'VNM', 'Vietnam'],
  ['vu', 'VUT', 'Vanuatu'],
  ['ws', 'WSM', 'Samoa'],
  ['xk', 'XKX', 'Kosovo'],
  ['ye', 'YEM', 'Yemen'],
  ['za', 'ZAF', 'South Africa'],
  ['zm', 'ZMB', 'Zambia'],
  ['zw', 'ZWE', 'Zimbabwe'],
];

/** Other names and codes feeds use for the same country. */
const ALIASES: Record<string, string> = {
  usa: 'us',
  unitedstatesofamerica: 'us',
  america: 'us',
  greatbritain: 'gb',
  britain: 'gb',
  uk: 'gb',
  chinesetaipei: 'tw',
  korea: 'kr',
  republicofkorea: 'kr',
  korearepublicof: 'kr',
  russianfederation: 'ru',
  czechia: 'cz',
  turkiye: 'tr',
  holland: 'nl',
  uae: 'ae',
  cotedivoire: 'ci',
  republicofireland: 'ie',
  macedonia: 'mk',
  swaziland: 'sz',
  burma: 'mm',
  timorleste: 'tl',
  vatican: 'va',
  democraticrepublicofthecongo: 'cd',
  congodr: 'cd',
  republicofthecongo: 'cg',
  capeverdeislands: 'cv',
  caboverde: 'cv',
  macao: 'mo',
  hongkongchina: 'hk',
  // Olympic codes a feed may use where they differ from ISO alpha-3.
  ger: 'de',
  ned: 'nl',
  sui: 'ch',
  rsa: 'za',
  por: 'pt',
  cro: 'hr',
  den: 'dk',
  gre: 'gr',
  ina: 'id',
  iri: 'ir',
  ksa: 'sa',
  lat: 'lv',
  mas: 'my',
  phi: 'ph',
  tpe: 'tw',
  uru: 'uy',
  vie: 'vn',
  zim: 'zw',
  bul: 'bg',
  chi: 'cl',
  slo: 'si',
  eng: 'gb-eng',
  sco: 'gb-sct',
  wal: 'gb-wls',
  nir: 'gb-nir',
};

const fold = (value: string) =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/&/g, 'and')
    .replace(/\bst\b\.?/g, 'saint')
    .replace(/[^a-z]/g, '');

const LOOKUP: ReadonlyMap<string, string> = (() => {
  const map = new Map<string, string>();
  for (const [code, alpha3, name] of COUNTRIES) {
    map.set(fold(name), code);
    if (!code.includes('-')) map.set(code, code);
    if (alpha3) map.set(alpha3.toLowerCase(), code);
  }
  for (const [alias, code] of Object.entries(ALIASES)) map.set(alias, code);
  return map;
})();

/** Every flag code there is artwork for. */
export const COUNTRY_FLAG_CODES: ReadonlySet<string> = new Set(COUNTRIES.map(([code]) => code));

/**
 * The flag code for a country as a feed describes it — a name in any case, an ISO alpha-2 or
 * alpha-3 code, or a common alias — or null when there is no flag for it ("Neutral", blank,
 * or a country with no artwork).
 */
export function countryToIso(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const lower = trimmed.toLowerCase();
  if (COUNTRY_FLAG_CODES.has(lower)) return lower;
  return LOOKUP.get(fold(trimmed)) ?? null;
}

/** The English name for a flag code, for alt text and tooltips. */
export function countryName(code: string | null | undefined): string | null {
  if (!code) return null;
  return COUNTRIES.find(([c]) => c === code.toLowerCase())?.[2] ?? null;
}

export type CountryFlagShape = 'round' | 'square';

/** The flag's path in the picks-core container: `flags/round/us.png`. */
export function countryFlagPath(code: string, shape: CountryFlagShape = 'round'): string {
  return `flags/${shape}/${code.toLowerCase()}.png`;
}

/** Sports whose competitors are shown by country flag rather than a club badge. */
export const COUNTRY_FLAG_SPORTS: ReadonlySet<string> = new Set(['MMA', 'TENNIS', 'NASCAR', 'PGA']);

export function usesCountryFlags(sport: string | null | undefined): boolean {
  return !!sport && COUNTRY_FLAG_SPORTS.has(sport.toUpperCase());
}
